import { FormItem, Section } from '../../../../api/types/expenseareas';

export type AnswerValue = string | string[];

export interface QuestionAnswer {
    id: number;
    answer: AnswerValue;
    required: boolean;
    error: boolean;
    errorMessage?: string;
    component_id: number | null;
    type?: string;
    name: string;
    description?: string;
    options?: any;
    component_name?: string;
}

export interface ExpenseAreaSection {
    id: string;
    name: string;
    order: number | null;
    description?: string;
}

export interface ExpenseAreaForRender {
    name: string;
    expense_area_id: number;
    order: number;
    sections: ExpenseAreaSection[];
    status?: 'active' | 'done' | 'pending';
    last_section_id: string | null;
    selected_components: number[];
    expense_area_name: string;
}

const flattenServerErrorsToPaths = (
    raw: unknown,
    parts: string[] = [],
    out: Record<string, string[]> = {}
): Record<string, string[]> => {
    if (raw == null) return out;

    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
        const path = parts.join('.');
        if (!path) return out;
        if (!out[path]) out[path] = [];
        out[path].push(String(raw));
        return out;
    }

    if (Array.isArray(raw)) {
        if (raw.length === 0) return out;
        if (raw.every((x) => x == null || ['string', 'number', 'boolean'].includes(typeof x))) {
            const path = parts.join('.');
            if (!path) return out;
            if (!out[path]) out[path] = [];
            for (const x of raw) out[path].push(String(x));
            return out;
        }
        raw.forEach((item, i) => flattenServerErrorsToPaths(item, [...parts, String(i)], out));
        return out;
    }

    if (typeof raw === 'object') {
        for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
            flattenServerErrorsToPaths(v, [...parts, k], out);
        }
    }
    return out;
};

const normalizeErrorsPayload = (raw: unknown): unknown => {
    if (!Array.isArray(raw)) return raw;

    const rows = raw.filter((x) => x && typeof x === 'object') as Record<string, unknown>[];
    if (
        rows.length > 0 &&
        rows.every((x) => {
            const key = x.field ?? x.key ?? x.attribute;
            return typeof key === 'string' && (x.message != null || x.messages != null || x.msg != null);
        })
    ) {
        const o: Record<string, string[]> = {};
        for (const x of rows) {
            const path = String(x.field ?? x.key ?? x.attribute ?? '');
            if (!path) continue;
            const msg = x.message ?? x.messages ?? x.msg;
            const list = Array.isArray(msg) ? msg.map(String) : [String(msg)];
            if (!o[path]) o[path] = [];
            o[path].push(...list.filter((s) => s && s.trim() !== ''));
        }
        return o;
    }

    return raw;
};

const extractFieldRowSpecifier = (path: string): number | null => {
    const patterns = [
        /\bfields\.(\d+)(?=\.|$|\[)/,
        /\bfields\[(\d+)\]/,
        /\bselected_components\.(\d+)(?=\.|$|\[)/,
        /\bselected_components\[(\d+)\]/
    ];
    for (const re of patterns) {
        const m = path.match(re);
        if (m) return parseInt(m[1], 10);
    }
    return null;
};

const resolveRowIndexForSpecifier = (spec: number, rows: QuestionAnswer[]): number | null => {
    if (spec >= 0 && spec < rows.length) return spec;
    const byId = rows.findIndex((r) => String(r.id) === String(spec));
    return byId >= 0 ? byId : null;
};

export const applyQuestionaryServerErrors = (
    prev: QuestionAnswer[],
    rawErrors: unknown
): { next: QuestionAnswer[]; unmapped: string[] } => {
    const cleared: QuestionAnswer[] = prev.map((item) => ({
        ...item,
        error: false,
        errorMessage: undefined
    }));

    if (rawErrors == null) {
        return { next: cleared, unmapped: [] };
    }

    if (typeof rawErrors === 'string') {
        const t = rawErrors.trim();
        return { next: cleared, unmapped: t ? [t] : [] };
    }

    if (Array.isArray(rawErrors) && rawErrors.length > 0 && rawErrors.every((x) => typeof x === 'string')) {
        const lines = (rawErrors as string[]).map((s) => s.trim()).filter(Boolean);
        return { next: cleared, unmapped: Array.from(new Set(lines)) };
    }

    const normalized = normalizeErrorsPayload(rawErrors);
    const flat = flattenServerErrorsToPaths(normalized);
    const next = [...cleared];
    const unmapped: string[] = [];
    const indexToMsgs = new Map<number, string[]>();

    for (const [path, msgs] of Object.entries(flat)) {
        const trimmed = msgs.map((m) => String(m).trim()).filter(Boolean);
        if (!trimmed.length) continue;

        const spec = extractFieldRowSpecifier(path);
        if (spec == null) {
            unmapped.push(...trimmed);
            continue;
        }
        const rowIndex = resolveRowIndexForSpecifier(spec, next);
        if (rowIndex == null) {
            unmapped.push(...trimmed);
            continue;
        }
        const prevMsg = indexToMsgs.get(rowIndex) ?? [];
        indexToMsgs.set(rowIndex, [...prevMsg, ...trimmed]);
    }

    for (const [rowIndex, messages] of indexToMsgs) {
        const text = Array.from(new Set(messages)).join('; ');
        next[rowIndex] = {
            ...next[rowIndex],
            error: true,
            errorMessage: text || 'Invalid value'
        };
    }

    return { next, unmapped: Array.from(new Set(unmapped)) };
};

export function extractApiErrorMessage(error: unknown, fallback: string): string {
    const err = error as { data?: { message?: string; error?: string } | string; message?: string; status?: number };
    if (err?.data && typeof err.data === 'object') {
        const fromData = err.data.message ?? err.data.error;
        if (fromData) return String(fromData);
    }
    if (typeof err?.data === 'string' && err.data.trim()) return err.data;
    if (err?.message) return err.message;
    if (err?.status) return `${fallback} (${err.status})`;
    return fallback;
}

/** API may return an array (supplier questionary) or a single expense-area object (admin preview). */
export function normalizeQuestionaryPayload(res: unknown, expenseAreaId: number): any[] {
    const raw = (res as { data?: unknown })?.data ?? res;

    if (Array.isArray(raw)) return raw;

    if (raw && typeof raw === 'object') {
        const obj = raw as Record<string, unknown>;
        if (Array.isArray(obj.questionary)) return obj.questionary;
        if (Array.isArray(obj.expense_areas)) return obj.expense_areas;

        return [
            {
                ...obj,
                expense_area_id: obj.expense_area_id ?? expenseAreaId
            }
        ];
    }

    return [];
}

/** Single expense-area payload from GET .../preview (includes formData, components, progressData). */
export function extractPreviewEaPayload(res: unknown, expenseAreaId: number): any | null {
    const items = normalizeQuestionaryPayload(res, expenseAreaId);
    if (!items.length) return null;
    return items.find((ea) => +ea.expense_area_id === +expenseAreaId) ?? items[0];
}

export function applyStatusByLastSection(data: any, goToSections: number[]): ExpenseAreaForRender[] {
    const list = Array.isArray(data) ? data : [];

    return list.map((elem: any) => {
        const lastSectionId = elem.progressData?.last_section_id ?? null;
        const sections = elem.formData?.sections ?? [];

        return {
            name: elem.expense_area_name ?? elem.name ?? '',
            expense_area_name: elem.expense_area_name ?? elem.name ?? '',
            description: elem.description,
            expense_area_id: elem.expense_area_id,
            last_section_id: lastSectionId,
            order: elem.order ?? 0,
            selected_components: elem.selected_components ?? [],
            sections: [
                {
                    id: `components-${elem.expense_area_id}`,
                    name: 'Service Components',
                    order: null
                },
                ...sections
                    .filter((section: Section) => {
                        if (!section.has_go_to) return true;
                        if (section.has_go_to && goToSections.includes(section.order)) return true;
                        return false;
                    })
                    .map((section: Section) => ({
                        name: section.title,
                        id: section.id + '',
                        order: section.order,
                        description: section.description
                    }))
            ]
        };
    });
}

export function collectGoToSectionsFromQuestionary(data: any[] | any): number[] {
    const goToSections: number[] = [];
    const list = Array.isArray(data) ? data : data ? [data] : [];

    list[0]?.formData?.sections?.forEach((elem: Section) => {
        elem.fields?.forEach((field: any) => {
            const go_to_section = field?.settings?.find((set: any) => set.type === 'go_to_section');
            if (go_to_section && go_to_section?.switchValue) {
                field.options?.forEach((opt: any) => {
                    if (opt.selected) {
                        goToSections.push(opt.go_to_next_section);
                    }
                });
            }
        });
    });
    return goToSections;
}

export function validateAnswers(data: QuestionAnswer[]) {
    let isValid = true;

    const updated = data.map((item) => {
        if (!item || typeof item !== 'object') return item;

        const isRequired = item.required;
        const ans = item.answer;

        const isEmptyString = ans === '';
        const isEmptyArray = Array.isArray(ans) && ans.length === 0;

        const hasClientError = isRequired && (isEmptyString || isEmptyArray);

        if (hasClientError) isValid = false;

        return {
            ...item,
            error: Boolean(hasClientError) || Boolean(item.errorMessage),
            errorMessage: hasClientError ? undefined : item.errorMessage
        };
    });

    return { updated, isValid };
}

export function buildSaveDataFromExpenseArea(activeSectionId: string, expenseAreaData: any): QuestionAnswer[] {
    if (activeSectionId.includes('components')) {
        const savedData: QuestionAnswer[] = [];
        const selectedComponents = expenseAreaData?.selected_components ?? [];

        expenseAreaData?.components?.forEach((elem: any) => {
            savedData.push({
                id: +elem.id,
                answer: selectedComponents.includes(elem.id) ? 'true' : 'false',
                required: false,
                error: false,
                component_id: elem.component_id,
                type: 'components',
                name: elem.name,
                description: elem.description
            });
        });

        return savedData;
    }

    const findSection = expenseAreaData?.formData?.sections.find(
        (elem: any) => elem.id + '' === activeSectionId + ''
    );
    if (!findSection) return [];

    const fields = findSection?.fields;
    const savedData: QuestionAnswer[] = [];

    fields?.forEach((elem: FormItem) => {
        const required = elem?.settings.find((setting) => setting?.type === 'required')?.switchValue ?? false;

        if (
            elem.type === 'short-answer' ||
            elem.type === 'paragraph' ||
            elem.type === 'single-select' ||
            elem.type === 'multiple-choice' ||
            elem.type === 'dropdown' ||
            elem.type === 'explanation-banner'
        ) {
            savedData.push({
                ...elem,
                id: +elem.id,
                answer: elem?.answer ? elem?.answer : '',
                required,
                error: false,
                name: elem.label
            } as QuestionAnswer);
        }
    });

    return savedData;
}

export function goToPrevSectionState(
    list: ExpenseAreaForRender[],
    activeEA: string,
    activeSectionId: string
): { activeEA: string; activeSectionId: string } | null {
    const currentEAIndex = list?.findIndex((elem) => elem.expense_area_id + '' === activeEA + '');
    const currentArea = list[currentEAIndex];
    const sections = currentArea?.sections;
    const prevAreaIndex = currentEAIndex === 0 ? 0 : currentEAIndex - 1;
    const prevAreaSections = list[prevAreaIndex].sections;
    const lastSection = prevAreaSections[prevAreaSections.length - 1];

    if (activeSectionId.includes('components')) {
        return {
            activeEA: list[prevAreaIndex]?.expense_area_id + '',
            activeSectionId: lastSection.id + ''
        };
    }

    const currentIndex = sections?.findIndex((s) => s.id + '' === activeSectionId + '');

    if (currentEAIndex === 0 && currentIndex === 0) return null;

    if (currentIndex != null && currentIndex > 0) {
        const prevSec = sections[currentIndex - 1];
        return { activeEA, activeSectionId: prevSec.id + '' };
    }

    if (currentIndex === 0) {
        const currentAreaId = list.find((elem) => elem.expense_area_id + '' === activeEA + '')?.expense_area_id;
        return {
            activeEA: currentAreaId + '',
            activeSectionId: `components-${currentAreaId}`
        };
    }

    return null;
}

export function goToNextSectionState(
    list: ExpenseAreaForRender[],
    activeEA: string,
    activeSectionId: string
): { activeEA: string; activeSectionId: string } | null {
    const currentAreaIndex = list.findIndex((elem) => elem.expense_area_id + '' === activeEA + '');
    const currentArea = list[currentAreaIndex];
    const sections = currentArea?.sections;

    const currentIndex = sections.findIndex((s) => s.id + '' === activeSectionId + '');
    const isLastSection = currentIndex === sections.length - 1;

    if (activeSectionId.includes('components')) {
        if (sections.length > 1) {
            const nextSec = sections[1];
            return { activeEA, activeSectionId: nextSec.id + '' };
        }
    }

    if (!isLastSection) {
        const nextSec = sections[currentIndex + 1];
        return { activeEA, activeSectionId: nextSec.id + '' };
    }

    const nextAreaIndex = currentAreaIndex + 1;
    const nextArea = list[nextAreaIndex];

    if (nextArea) {
        const nextAreaId = nextArea.expense_area_id;
        return {
            activeEA: nextAreaId + '',
            activeSectionId: `components-${nextAreaId}`
        };
    }

    return null;
}

export function isLastQuestionarySection(list: ExpenseAreaForRender[], activeEA: string, activeSectionId: string) {
    const lastEAindex = list.length - 1;
    const lastSectionIndex = list[lastEAindex]?.sections.length - 1;
    const lastSectionIdLastEAId = list[lastEAindex]?.sections[lastSectionIndex]?.id;
    return activeSectionId + '' === lastSectionIdLastEAId + '';
}
